import { useMemo, useCallback, useState } from "react";
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
  GridPagination,
} from "@mui/x-data-grid";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Link,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import { predictionManagerAxiosApi } from "src/Config/axiosApi";
import { MatchedByType, PredictionOpportunity } from "../../types";
import { usePredictions } from "../../context";
import { useUpdatePairThreshold } from "../../api/usePositions";

interface Props {
  data: PredictionOpportunity[];
  isLoading?: boolean;
}

function CustomPagination(props: any) {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Box display="flex" alignItems="center">
      <IconButton
        onClick={() => apiRef.current.setPage(0)}
        disabled={page === 0}
      >
        <FirstPageIcon />
      </IconButton>
      <GridPagination {...props} />
      <IconButton
        onClick={() => apiRef.current.setPage(pageCount - 1)}
        disabled={page === pageCount - 1}
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

const formatPercent = (value: number) => {
  if (value == null || isNaN(value)) return "-";
  return (value * 100).toFixed(2) + "%";
};

const formatDecimal = (value: number, digits = 4) => {
  if (value == null || isNaN(value)) return "-";
  return value.toFixed(digits);
};

const platformLabel = (platform: string) => {
  const map: Record<string, string> = {
    polymarket: "Poly",
    kalshi: "Kalshi",
  };
  return map[platform] || platform;
};

const matchColor = (matchedBy: string): "success" | "info" | "warning" | "secondary" | "default" => {
  switch (matchedBy) {
    case "manual":
      return "success";
    case "ai_confirmed":
    case "keyword_confirmed":
      return "info";
    case "ai_pending":
    case "keyword_pending":
      return "warning";
    default:
      return "default";
  }
};

const matchLabel = (matchedBy: string): string => {
  const labels: Record<string, string> = {
    manual: "manual",
    ai_confirmed: "ai",
    ai_pending: "ai?",
    keyword_pending: "kw?",
    keyword_confirmed: "kw",
    semantic_fallback: "sem",
    semantic_confirmed: "sem",
    structured_match: "struct",
    structured_confirmed: "struct",
  };
  return labels[matchedBy] || matchedBy;
};

const confirmedMatchedBy = (current: MatchedByType): MatchedByType => {
  if (current === "ai_pending") return "ai_confirmed";
  if (current === "keyword_pending") return "keyword_confirmed";
  return current;
};

export const PredictionsTable = ({ data, isLoading }: Props) => {
  const [unmatchingId, setUnmatchingId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({ open: false, message: "", severity: "success" });
  const { removePair, confirmPair } = usePredictions();
  const { mutate: updateThreshold } = useUpdatePairThreshold();
  const [thresholdInputs, setThresholdInputs] = useState<Record<string, string>>({});

  const handleThresholdConfirm = useCallback((pairId: string) => {
    const value = thresholdInputs[pairId] ?? "";
    const num = parseFloat(value);
    updateThreshold(
      { pairId, threshold: isNaN(num) ? 0 : num },
      {
        onSuccess: () => setSnackbar({ open: true, message: "Threshold updated", severity: "success" }),
        onError: () => setSnackbar({ open: true, message: "Failed to update threshold", severity: "error" }),
      }
    );
  }, [updateThreshold, thresholdInputs]);

  const handleUnmatch = useCallback(async (pairId: string) => {
    if (!confirm("Unmatch this pair? This will prevent re-matching and will try to find a new match.")) return;

    setUnmatchingId(pairId);
    try {
      await predictionManagerAxiosApi.patch(`/pairs/${pairId}/reject`);
      removePair(pairId);
    } catch (err) {
      console.error("Failed to unmatch pair:", err);
    } finally {
      setUnmatchingId(null);
    }
  }, [removePair]);

  const handleConfirm = useCallback(async (pairId: string, currentMatchedBy: MatchedByType) => {
    setConfirmingId(pairId);
    try {
      await predictionManagerAxiosApi.patch(`/pairs/${pairId}/confirm`);
      const newMatchedBy = confirmedMatchedBy(currentMatchedBy);
      confirmPair(pairId, newMatchedBy);
      setSnackbar({ open: true, message: "Match confirmed", severity: "success" });
    } catch (err) {
      console.error("Failed to confirm pair:", err);
      setSnackbar({ open: true, message: "Failed to confirm match", severity: "error" });
    } finally {
      setConfirmingId(null);
    }
  }, [confirmPair]);

  const columns: GridColDef<PredictionOpportunity>[] = useMemo(
    () => [
      {
        headerName: "Event",
        field: "pairName",
        type: "string",
        flex: 1.5,
        minWidth: 160,
        renderCell: ({ row }) => {
          let yesTitle = row.direction?.yesTitle;
          let noTitle = row.direction?.noTitle;
          // Fallback: split pairName "polymarket_title ↔ kalshi_title" and map by platform
          if (!yesTitle || !noTitle) {
            const parts = row.pairName.split("↔").map((s: string) => s.trim());
            const polyTitle = parts[0] || row.pairName;
            const kalshiTitle = parts[1] || polyTitle;
            const yesPlatform = row.direction?.yesPlatform;
            yesTitle = yesTitle || (yesPlatform === "polymarket" ? polyTitle : kalshiTitle);
            noTitle = noTitle || (yesPlatform === "polymarket" ? kalshiTitle : polyTitle);
          }
          const yesUrl = row.direction?.yesMarketUrl;
          const noUrl = row.direction?.noMarketUrl;
          const yesPlatformLbl = platformLabel(row.direction?.yesPlatform);
          const noPlatformLbl = platformLabel(row.direction?.noPlatform);
          return (
            <Tooltip title={`${yesPlatformLbl}: ${yesTitle}\n${noPlatformLbl}: ${noTitle}`} placement="bottom-start">
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", py: 0.5, overflow: "hidden" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {yesUrl ? (
                    <Link
                      href={yesUrl}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32, cursor: "pointer" }}
                    >
                      {yesPlatformLbl}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32 }}>
                      {yesPlatformLbl}
                    </Typography>
                  )}
                  <Typography variant="body2" noWrap sx={{ fontSize: "0.75rem", lineHeight: 1.3 }}>
                    {yesTitle}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {noUrl ? (
                    <Link
                      href={noUrl}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32, cursor: "pointer" }}
                    >
                      {noPlatformLbl}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: "0.65rem", fontWeight: 600, color: "text.secondary", minWidth: 32 }}>
                      {noPlatformLbl}
                    </Typography>
                  )}
                  <Typography variant="body2" noWrap sx={{ fontSize: "0.75rem", lineHeight: 1.3, color: "text.secondary" }}>
                    {noTitle}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        headerName: "YES @",
        field: "direction.yesPlatform",
        type: "string",
        width: 60,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.yesPlatform,
        renderCell: ({ row, value }) => {
          const url = row.direction?.yesMarketUrl;
          const img = (
            <img
              src={`/assets/exchanges/png/100x100/${value}.png`}
              alt={value}
              style={{ width: 24, height: 24, borderRadius: "50%" }}
            />
          );
          return (
            <Tooltip title={platformLabel(value)} placement="top">
              {url ? (
                <a href={url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                  {img}
                </a>
              ) : img}
            </Tooltip>
          );
        },
      },
      {
        headerName: "NO @",
        field: "direction.noPlatform",
        type: "string",
        width: 60,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.noPlatform,
        renderCell: ({ row, value }) => {
          const url = row.direction?.noMarketUrl;
          const img = (
            <img
              src={`/assets/exchanges/png/100x100/${value}.png`}
              alt={value}
              style={{ width: 24, height: 24, borderRadius: "50%" }}
            />
          );
          return (
            <Tooltip title={platformLabel(value)} placement="top">
              {url ? (
                <a href={url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                  {img}
                </a>
              ) : img}
            </Tooltip>
          );
        },
      },
      {
        headerName: "Ask YES",
        field: "direction.yesAsk",
        type: "number",
        width: 80,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.yesAsk,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {formatDecimal(value)}
          </Typography>
        ),
      },
      {
        headerName: "Ask NO",
        field: "direction.noAsk",
        type: "number",
        width: 80,
        align: "center",
        headerAlign: "center",
        valueGetter: (params: GridValueGetterParams) => params.row.direction?.noAsk,
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {formatDecimal(value)}
          </Typography>
        ),
      },
      {
        headerName: "Cost",
        field: "cost",
        type: "number",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: value < 0.95 ? "#4caf50" : value < 0.99 ? "#ff9800" : "inherit",
              fontWeight: value < 0.95 ? 700 : 400,
            }}
          >
            {formatDecimal(value)}
          </Typography>
        ),
      },
      {
        headerName: "Profit %",
        field: "profitPct",
        type: "number",
        width: 85,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: "#4caf50",
              fontWeight: 600,
            }}
          >
            {formatPercent(value)}
          </Typography>
        ),
      },
      {
        headerName: "Days",
        field: "daysToExpiry",
        type: "number",
        width: 60,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }: any) => {
          if (value == null) return '';
          return value < 1 ? value.toFixed(1) : Math.round(value);
        },
      },
      {
        headerName: "Maker TNA",
        field: "makerTna",
        type: "number",
        width: 95,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: value > 0.5 ? "#4caf50" : value > 0.2 ? "#ff9800" : "inherit",
            }}
          >
            {formatPercent(value)}
          </Typography>
        ),
      },
      {
        headerName: "Taker TNA",
        field: "takerTna",
        type: "number",
        width: 95,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: value > 0.5 ? "#4caf50" : value > 0.2 ? "#ff9800" : "inherit",
            }}
          >
            {formatPercent(value)}
          </Typography>
        ),
      },
      {
        headerName: "Liquidity",
        field: "maxFillableContracts",
        type: "number",
        width: 90,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
            {value == null || isNaN(value) ? "-" : Math.floor(value).toLocaleString()}
          </Typography>
        ),
      },
      {
        headerName: "Match",
        field: "matchConfidence",
        type: "number",
        width: 120,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const tooltipText = row.keywordOverlap?.length
            ? `Keywords: ${row.keywordOverlap.join(", ")}`
            : row.matchedBy;
          const confidenceText = row.matchConfidence != null
            ? `${Math.round(row.matchConfidence * 100)}%`
            : null;

          return (
            <Tooltip title={tooltipText} placement="top">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Chip
                  label={matchLabel(row.matchedBy)}
                  size="small"
                  color={matchColor(row.matchedBy) as any}
                  sx={{ fontSize: "0.65rem" }}
                />
                {confidenceText && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: row.matchConfidence! >= 0.8 ? "success.main" : row.matchConfidence! >= 0.5 ? "warning.main" : "error.main",
                      fontWeight: 600,
                    }}
                  >
                    {confidenceText}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Max $",
        field: "maxInvestmentThreshold",
        type: "number",
        width: 90,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const value = thresholdInputs[row.pairId] ?? "";
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <TextField
                size="small"
                placeholder="0"
                value={value}
                onChange={(e) => setThresholdInputs(prev => ({ ...prev, [row.pairId]: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Enter") handleThresholdConfirm(row.pairId); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '0.7rem', color: 'grey.600' } }}>$</InputAdornment>,
                }}
                sx={{
                  width: 70,
                  '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.4, px: 0.3, MozAppearance: 'textfield', '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { display: 'none' } },
                  '& .MuiOutlinedInput-root': { height: 28 },
                }}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleThresholdConfirm(row.pairId); }}
                sx={{ p: 0.3, color: "grey.500", '&:hover': { color: "success.main" } }}
              >
                <CheckIcon sx={{ fontSize: "0.85rem" }} />
              </IconButton>
            </Box>
          );
        },
      },
      {
        headerName: "",
        field: "actions",
        type: "string",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const isUnmatching = unmatchingId === row.pairId;
          const isConfirming = confirmingId === row.pairId;
          const isPending = row.matchedBy === "ai_pending" || row.matchedBy === "keyword_pending";
          return (
            <Box sx={{ display: "flex", gap: 0.25 }}>
              {isPending && (
                <Tooltip title="Confirm match" placement="top">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleConfirm(row.pairId, row.matchedBy)}
                      disabled={isConfirming}
                      sx={{ color: "success.main" }}
                    >
                      {isConfirming ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CheckCircleOutlineIcon sx={{ fontSize: "1rem" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Unmatch pair" placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleUnmatch(row.pairId)}
                    disabled={isUnmatching}
                    sx={{ color: "grey.500" }}
                  >
                    {isUnmatching ? (
                      <CircularProgress size={16} />
                    ) : (
                      <LinkOffIcon sx={{ fontSize: "1rem" }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [unmatchingId, confirmingId, handleUnmatch, handleConfirm, thresholdInputs, handleThresholdConfirm]
  );

  return (
    <>
    <DataGrid
      rows={data}
      columns={columns}
      getRowId={(row) => row.pairId}
      loading={isLoading}
      autoHeight
      rowHeight={52}
      disableSelectionOnClick
      initialState={{
        sorting: {
          sortModel: [
          { field: "matchConfidence", sort: "desc" },
          { field: "takerTna", sort: "desc" },
        ],
        },
        pagination: {
          pageSize: 25,
        },
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      components={{
        Pagination: CustomPagination,
      }}
      sx={{
        border: "none",
        "& .MuiDataGrid-cell": {
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        },
        "& .MuiDataGrid-columnHeaders": {
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "rgba(255,255,255,0.03)",
        },
      }}
    />
    <Snackbar
      open={snackbar.open}
      autoHideDuration={3000}
      onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
};
